// useConfirm — единое окно подтверждения вместо нативных window.confirm/prompt.
// Vanilla reactive (без pinia): состояние модуля + Promise-контракт, как у студии.
// В App монтируется <ConfirmHost /> один раз; из любого компонента:
//   const { confirm } = useConfirm();
//   const res = await confirm({ title: 'Удалить?', danger: true });
//   if (!res.confirmed) return;
//   const slug = await confirm({ title: '…', input: { label: 'slug' } }); // value в res.value
import { reactive } from 'vue';

export interface ConfirmInput {
  /** Подпись поля (необязательно). */
  label?: string;
  /** Плейсхолдер поля. */
  placeholder?: string;
}

export interface ConfirmOptions {
  /** Заголовок окна (короткий вопрос). */
  title: string;
  /** Пояснение: что произойдёт, необратимость, детали. */
  message?: string;
  /** Подпись кнопки подтверждения (дефолт «Подтвердить»). */
  confirmLabel?: string;
  /** Подпись кнопки отмены (дефолт «Отмена»). */
  cancelLabel?: string;
  /** Опасное действие — красная кнопка и иконка-предупреждение. */
  danger?: boolean;
  /** Кнопки заблокированы (мутация в полёте). */
  busy?: boolean;
  /** Необязательное поле ввода (причина, пароль, slug-подтверждение). */
  input?: ConfirmInput;
}

export interface ConfirmResult {
  /** true — подтверждено, false — отмена/Esc/клик вне. */
  confirmed: boolean;
  /** Введённое значение (только если задан input и подтверждено). */
  value?: string;
}

const state = reactive<{ open: boolean; options: ConfirmOptions }>({ open: false, options: { title: '' } });
let pending: ((v: ConfirmResult) => void) | null = null;

/** Показать окно и ждать решения. */
function ask(opts: ConfirmOptions): Promise<ConfirmResult> {
  state.options = { confirmLabel: 'Подтвердить', cancelLabel: 'Отмена', danger: false, ...opts };
  state.open = true;
  return new Promise<ConfirmResult>((resolve) => { pending = resolve; });
}

/** Вызывается ConfirmHost'ом. */
function settle(confirmed: boolean, value?: string): void {
  state.open = false;
  pending?.({ confirmed, value: confirmed ? value : undefined });
  pending = null;
}

/** Единая точка входа: const { confirm } = useConfirm(). */
export function useConfirm() {
  return { confirm: (opts: ConfirmOptions) => ask(opts) };
}

/** Экспорт состояния для ConfirmHost (внутреннее, но нужно для рендера). */
export function confirmState() {
  return { state, settle };
}